import { AerTooltip } from "@aerstudios/aerated-aertooltip";
import cx from "classnames";
import React from "react";
import { type PropsWithChildren, useEffect, useState } from "react";
import { Info } from "../common/Icons";
import * as styles from "./Tooltip.module.scss";

interface Props extends PropsWithChildren {
	text: string;
	/** This id is the same as the id of the sibling form element. Internally it will be appended with "-tooltip". */
	id: string;
}

export const Tooltip: React.FC<Props> = ({ text, id }) => {
	const [showText, setShowText] = useState(false);

	// Hide tooltip content if clicking outside of the popup
	document.addEventListener("click", () => setShowText(false));

	useEffect(() => {
		function handleKeydown(event: KeyboardEvent) {
			if (event.key === "Escape" && showText) {
				setShowText(false);
			}
		}

		document.addEventListener("keyup", handleKeydown);

		return () => {
			document.removeEventListener("keyup", handleKeydown);
		};
	}, [showText]);

	const tooltipId = `${id}-tooltip`;

	return (
		<AerTooltip.Root open={showText} delayDuration={0}>
			<AerTooltip.Trigger
				onClick={(e) => {
					e.preventDefault();
					e.stopPropagation();
					setShowText((v) => !v);
				}}
				aria-expanded={showText}
				aria-controls={tooltipId}
				aria-label="View more information about this field"
				className={styles.button}
			>
				<Info
					className={cx(styles.icon, showText && styles.active)}
					width="18px"
					height="18px"
					fill="currentColor"
				/>
			</AerTooltip.Trigger>
			<AerTooltip.Content
				className={styles.content}
				asChild
				align="end"
				side="bottom"
			>
				<div role="region" aria-live="polite" id={tooltipId}>
					{showText && (
						<div
							className={cx(styles.tooltip)}
							// biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
							dangerouslySetInnerHTML={{ __html: text }}
						/>
					)}
				</div>
			</AerTooltip.Content>
		</AerTooltip.Root>
	);
};

export default Tooltip;
